import { TFunction } from 'i18next';
import { z } from 'zod';

import { StepAdditionalDataFields } from './components/StepAdditionalDataFields';
import { StepConfirmationFields } from './components/StepConfirmationFields';
import { StepForFields } from './components/StepForFields';
import { StepRecipientFields } from './components/StepRecipientFields';
import { StepSendFields } from './components/StepSendFields';
import { defaultValues } from './defaultValues';
import {
  getStep1Schema,
  getStep2Schema,
  getStep3Schema,
  getStep4Schema,
  step5Schema,
} from './schema';
import type {
  InvitationCreateFormValues,
  StepConfig,
  WizardStepKey,
} from './types';

type InvitationWizardT = TFunction<['common', 'invitations']>;

const fallbackT = ((key: string) => key) as InvitationWizardT;

const getWizardSchema = (t: InvitationWizardT) =>
  z.object({
    step1: getStep1Schema(t),
    step2: getStep2Schema(t),
    step3: getStep3Schema(t),
    step4: getStep4Schema(t),
    step5: step5Schema,
  });

export const wizardSchema = getWizardSchema(fallbackT);

export { defaultValues };

const canAdvanceWhenStepValid = <TStepKey extends WizardStepKey>(
  stepKey: TStepKey,
  stepSchema: z.ZodType<InvitationCreateFormValues[TStepKey]>,
) => {
  return (values: InvitationCreateFormValues) =>
    stepSchema.safeParse(values[stepKey]).success;
};

export const getStepConfigs = (
  t: TFunction<['common', 'invitations']>,
): StepConfig[] => [
  {
    Component: StepForFields,
    canAdvance: canAdvanceWhenStepValid('step1', getStep1Schema(t)),
    key: 'step1',
    label: t('invitations:for'),
  },
  {
    Component: StepRecipientFields,
    canAdvance: canAdvanceWhenStepValid('step2', getStep2Schema(t)),
    key: 'step2',
    label: t('invitations:to'),
  },
  {
    Component: StepAdditionalDataFields,
    canAdvance: canAdvanceWhenStepValid('step3', getStep3Schema(t)),
    key: 'step3',
    label: t('invitations:optionalData'),
  },
  {
    Component: StepConfirmationFields,
    canAdvance: canAdvanceWhenStepValid('step4', getStep4Schema(t)),
    key: 'step4',
    label: t('invitations:title.confirmation'),
  },
  {
    Component: StepSendFields,
    key: 'step5',
    label: t('invitations:send'),
  },
];
